/**
 * Analytics utility for tracking visitor information
 * Supports both Google Analytics 4 and custom backend tracking
 */

import api from '../services/api';

// Google Analytics 4 Measurement ID (set via environment variable)
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

/**
 * Initialize Google Analytics 4
 */
export const initGA = () => {
  if (!GA_MEASUREMENT_ID || typeof window === 'undefined') {
    return;
  }

  // Load gtag script if not already loaded
  if (!window.gtag) {
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${GA_MEASUREMENT_ID}', {
        page_path: window.location.pathname,
      });
    `;
    document.head.appendChild(script2);
  }
};

/**
 * Track page view for Google Analytics
 */
export const trackPageView = (path) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
    });
  }
};

/**
 * Track custom event for Google Analytics
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

/**
 * Track custom event to Laravel backend
 * Uses the api instance to automatically include auth token if user is logged in
 */
export const trackToBackend = async (eventType, eventData = {}) => {
  try {
    // Get token explicitly - check both storage locations
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    
    // Debug logging
    if (!token) {
      console.warn('Analytics: No auth token found in localStorage or sessionStorage');
    } else {
      console.log('Analytics: Token found, length:', token.length);
    }
    
    // Get visitor information
    const visitorData = {
      event_type: eventType,
      page: window.location.pathname,
      referrer: document.referrer || '',
      user_agent: navigator.userAgent,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: new Date().toISOString(),
      ...eventData,
    };

    // Get API base URL
    const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://api.guyrules.wpsee.com/api';

    // Build headers with explicit token if available
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    
    // ALWAYS add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      console.log('Analytics: Adding Authorization header with token');
    } else {
      console.warn('Analytics: No token available, request will be sent without auth');
    }

    // Use fetch with explicit headers to ensure token is sent
    // This is more reliable than relying on axios interceptor
    fetch(`${apiBaseUrl}/analytics/track`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(visitorData),
    })
      .then(response => {
        console.log('Analytics: Response status:', response.status);
        return response.json();
      })
      .then(data => {
        if (!data.success) {
          console.warn('Analytics tracking failed:', data.message);
        } else {
          console.log('Analytics: Event tracked successfully', {
            event_type: eventType,
            user_id: data.debug?.user_id,
            has_debug: !!data.debug,
          });
        }
      })
      .catch((error) => {
        // Log errors for debugging (but don't break the app)
        console.error('Analytics tracking error:', error);
      });
  } catch (error) {
    // Log errors
    console.error('Analytics tracking exception:', error);
  }
};

/**
 * Track page view to both GA and backend
 */
export const trackPageViewFull = (path) => {
  trackPageView(path);
  trackToBackend('page_view', { page: path });
};

/**
 * Track button click or user interaction
 */
export const trackInteraction = (action, element, additionalData = {}) => {
  trackEvent('interaction', {
    action,
    element,
    ...additionalData,
  });
  
  trackToBackend('interaction', {
    action,
    element,
    ...additionalData,
  });
};

/**
 * Track form submission
 */
export const trackFormSubmit = (formName, success = true, additionalData = {}) => {
  trackEvent('form_submit', {
    form_name: formName,
    success,
    ...additionalData,
  });
  
  trackToBackend('form_submit', {
    form_name: formName,
    success,
    ...additionalData,
  });
};

/**
 * Track search query
 */
export const trackSearch = (query, resultsCount = 0) => {
  trackEvent('search', {
    search_term: query,
    results_count: resultsCount,
  });
  
  trackToBackend('search', {
    search_term: query,
    results_count: resultsCount,
  });
};

/**
 * Track rule view
 */
export const trackRuleView = (ruleId, ruleNumber) => {
  trackEvent('view_rule', {
    rule_id: ruleId,
    rule_number: ruleNumber,
  });
  
  trackToBackend('rule_view', {
    rule_id: ruleId,
    rule_number: ruleNumber,
  });
};

/**
 * Track quiz completion
 */
export const trackQuizComplete = (score, totalQuestions) => {
  trackEvent('quiz_complete', {
    score,
    total_questions: totalQuestions,
    percentage: Math.round((score / totalQuestions) * 100),
  });
  
  trackToBackend('quiz_complete', {
    score,
    total_questions: totalQuestions,
    percentage: Math.round((score / totalQuestions) * 100),
  });
};
